import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { tokens, Alert } from '@hotjar/hotsauce';
import type { Site } from '@hotjar/site';
import { useHelpHotjarUrl } from '@hotjar/translations';

import { TrafficCoverageSumUp } from './TrafficCoverageSumUp';
import { TrafficCoverageUpgrade } from './TrafficCoverageUpgrade';
import { TrafficCoverageSettings } from './TrafficCoverageSettings';
import { TrafficCoverageIdentifyUaInfo } from './TrafficCoverageIdentifyUaInfo';

const Container = styled.div``;

const StyledAlert = styled(Alert)`
  flex-flow: wrap;
  & > div:first-of-type {
    width: 70%;
  }

  & > button:first-of-type {
    margin-left: ${tokens.spacer.x4};
  }

  border-radius: 0;
  border-width: 0 0 1px 0;
`;

const FullCoverageAlert = styled(Alert)`
  border-radius: 0;
`;

interface Props {
  site: Site | undefined;
  isCoverageMeasureComplete: boolean;
  hasPartialMeasure: boolean;
  coveragePercentage: number;
  dailyTraffic: number;
  ccDailyAllowance: number;
  userCanContactSales: boolean;
  trackClick: (elementName: string, shownTrafficCoverage?: number | null) => void;
  disableAnimation?: boolean;
  isDropDownShown: boolean;
}

const HOTJAR_CAPTURE_TRAFFIC_LINK = '/articles/115011624047';

export const TrafficCoverageMessage = ({
  site,
  isCoverageMeasureComplete,
  hasPartialMeasure,
  coveragePercentage,
  dailyTraffic,
  ccDailyAllowance,
  userCanContactSales,
  trackClick,
  disableAnimation = false,
  isDropDownShown,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const incompleteMeasureStateI18nContext = hasPartialMeasure ? 'partial' : 'incomplete';

  return (
    <Container>
      {isCoverageMeasureComplete && coveragePercentage === 100 && (
        <FullCoverageAlert
          kind="success"
          title={t('common:sampling-widget.tracking-coverage-message.full-covering-title')}
        >
          {t('common:sampling-widget.tracking-coverage-message.full-covering-body')}
        </FullCoverageAlert>
      )}
      {!isCoverageMeasureComplete && (
        <StyledAlert
          kind="info"
          title={t('common:sampling-widget.tracking-coverage-message.currently-measuring-title', {
            context: incompleteMeasureStateI18nContext,
          })}
          actionText={t('common:sampling-widget.tracking-coverage-message.learn-more')}
          onAction={() => {
            window.open(
              `${helpHotjarUrl}${HOTJAR_CAPTURE_TRAFFIC_LINK}`,
              '_blank',
              'noopener noreferrer',
            );
            trackClick('measuring_in_progress_top-learn-more-link', coveragePercentage);
          }}
        >
          {dailyTraffic === 0
            ? t(
                'common:sampling-widget.tracking-coverage-message.currently-measuring-without-percentage-body',
                {
                  context: incompleteMeasureStateI18nContext,
                },
              )
            : t('common:sampling-widget.tracking-coverage-message.currently-measuring-body', {
                context: incompleteMeasureStateI18nContext,
              })}
        </StyledAlert>
      )}

      <TrafficCoverageSumUp
        site={site}
        isCoverageMeasureComplete={isCoverageMeasureComplete}
        hasPartialMeasure={hasPartialMeasure}
        coveragePercentage={coveragePercentage}
        dailyTraffic={dailyTraffic}
        ccDailyAllowance={ccDailyAllowance}
        trackClick={trackClick}
        disableAnimation={disableAnimation}
        isDropDownShown={isDropDownShown}
      />
      <TrafficCoverageUpgrade
        isCoverageMeasureComplete={hasPartialMeasure || isCoverageMeasureComplete}
        coveragePercentage={coveragePercentage}
        trackClick={trackClick}
        userCanContactSales={userCanContactSales}
      />
      <TrafficCoverageSettings
        trackClick={trackClick}
        coveragePercentage={coveragePercentage}
        isCoverageMeasureComplete={hasPartialMeasure || isCoverageMeasureComplete}
      />
      {isCoverageMeasureComplete && (
        <TrafficCoverageIdentifyUaInfo
          trackClick={trackClick}
          coveragePercentage={coveragePercentage}
          isCoverageMeasureComplete={hasPartialMeasure || isCoverageMeasureComplete}
        />
      )}
    </Container>
  );
};
